import { useMemo, useState } from 'react';
import { Plus, X, Check, ChevronsUpDown, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { SELECTED_OUTLINE_BUTTON } from '@/components/custom/highlight';
import {
  ASSIGNMENT_RULE_MAX_CHILDREN,
  ASSIGNMENT_RULE_MAX_DEPTH,
  ruleNodeDepth,
  type AssignmentRuleField,
  type AssignmentRuleNode,
  type ConditionOperator,
} from '@vagusx/shared';
import { useLabelOptions } from '@/lib/select-label-options';
import { useConditionValueResolver } from '@/hooks/use-condition-value-resolver';
import { ProviderPicker } from './provider-picker';
import { LocationPicker } from './location-picker';
import { InsurancePicker } from './insurance-picker';

// =============================================================================
// Field & Operator Definitions
// =============================================================================

const FIELDS = [
  { value: 'priority', label: 'Priority' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'provider', label: 'Provider' },
  { value: 'diagnoses', label: 'Diagnoses' },
  { value: 'services', label: 'Services' },
  { value: 'location', label: 'Location' },
  { value: 'label', label: 'Label' },
] as const satisfies readonly { value: AssignmentRuleField; label: string }[];

const OPERATORS_BY_FIELD: Record<
  AssignmentRuleField,
  { value: ConditionOperator; label: string }[]
> = {
  priority: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'in', label: 'is one of' },
  ],
  insurance: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'in', label: 'is one of' },
  ],
  provider: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'in', label: 'is one of' },
  ],
  diagnoses: [
    { value: 'contains_any', label: 'contains any of' },
    { value: 'contains_all', label: 'contains all of' },
  ],
  services: [
    { value: 'contains_any', label: 'contains any of' },
    { value: 'contains_all', label: 'contains all of' },
  ],
  location: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'in', label: 'is one of' },
  ],
  // Label is exclusively multi-value — `in` / `not_in` only.
  label: [
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
  ],
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// =============================================================================
// Editor-internal types
// =============================================================================
//
// The editor's working tree is structurally a SUPERSET of the wire
// `AssignmentRuleNode`: a fresh row from "+ Condition" hasn't picked a
// field/operator yet, so those slots hold `null` while the user is
// still filling them in. Modeling this explicitly lets us drop the
// `'' as AssignmentRuleField` lies that the previous version used —
// the type system now accurately describes what's possible at each
// edit stage.
//
// On load, an `AssignmentRuleNode` from the API is structurally
// compatible with `EditorNode` (field/operator are never null on
// the wire), so the load path is a free up-cast. On save, we narrow
// with `hasAnyConfiguredCondition` first; the cast at the boundary
// in `RuleEditor.tsx` is then justified by that runtime check.

export interface EditorCondition {
  kind: 'condition';
  field: AssignmentRuleField | null;
  operator: ConditionOperator | null;
  value: string | string[];
}

export interface EditorGroup {
  kind: 'group';
  op: 'all' | 'any';
  children: EditorNode[];
}

export type EditorNode = EditorCondition | EditorGroup;

/**
 * Empty leaf placeholder. Created when the user clicks "+ Condition";
 * rendered in a "select a field..." state until they pick something.
 * The save validator (`hasAnyConfiguredCondition`) requires at least
 * one fully-typed leaf before the form submits.
 */
function emptyCondition(): EditorCondition {
  return { kind: 'condition', field: null, operator: null, value: '' };
}

/**
 * Empty nested group. Created when the user clicks "+ Group". Comes
 * pre-seeded with one empty condition so it's never an empty
 * container — empty groups are an invalid save state per the route's
 * Zod refines.
 */
function emptyGroup(op: 'all' | 'any' = 'all'): EditorGroup {
  return { kind: 'group', op, children: [emptyCondition()] };
}

/**
 * Per-leaf completeness check. A leaf is complete iff it has both
 * field and operator picked AND a non-empty value (non-empty string,
 * or non-empty array for multi-value operators).
 *
 * This is the authoritative client-side check — the same predicate
 * runs server-side via `hasIncompleteLeaf` from @vagusx/shared.
 */
export function isLeafComplete(node: EditorCondition): boolean {
  if (!node.field || !node.operator) return false;
  if (Array.isArray(node.value)) return node.value.length > 0;
  return node.value !== '';
}

/**
 * True iff every leaf in the tree is complete AND every group has at
 * least one child. Gates the save button: a partial rule (any leaf
 * with missing field/operator/value, or any empty group) is rejected
 * before reaching the API.
 *
 * Replaces the looser `hasAnyConfiguredCondition` predicate, which
 * only checked that *some* leaf was configured — that allowed the
 * user to ship rules where one leaf was set and others were
 * placeholders, producing partial saves the API then accepted.
 */
export function areAllLeavesConfigured(node: EditorNode): boolean {
  if (node.kind === 'condition') return isLeafComplete(node);
  if (node.children.length === 0) return false;
  return node.children.every(areAllLeavesConfigured);
}

// =============================================================================
// Label Multi-Select (uses label name as id)
// =============================================================================

function LabelMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  // Reuses the same options pipeline the FilterBar consumes — labels
  // come back as `{ value, label, color }` with the color resolved
  // to a Tailwind bg-* class via `labelColorClass`. Single source of
  // truth for the dropdown rendering across editor + filters.
  const options = useLabelOptions();

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  const toggle = (id: string): void => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  // Picker on top, selected badges hang below. Keeps the picker as
  // the first row of the value column so the row-remove `×` (top-right
  // of the parent grid, items-start) lines up with the picker rather
  // than with chips above it.
  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            className="h-9 w-full justify-between text-xs font-normal"
          >
            <span className="truncate text-muted-foreground">Select label...</span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command
            filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
          >
            <CommandInput placeholder="Search labels..." />
            <CommandList>
              <CommandEmpty>No labels found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                    className="gap-2"
                  >
                    {/* Colored bullet on the left, matching the
                        tasks FilterBar dropdown — single visual
                        idiom for "this is a label" across editors. */}
                    {option.color && (
                      <span
                        className={cn('inline-block size-2 shrink-0 rounded-full', option.color)}
                      />
                    )}
                    <span className="truncate">{option.label}</span>
                    {selected.includes(option.value) && (
                      <Check className="ml-auto size-3 text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((opt) => (
            <Badge key={opt.value} variant="secondary" className="gap-1 py-0 text-xs">
              {opt.color && (
                <span className={cn('inline-block size-1.5 shrink-0 rounded-full', opt.color)} />
              )}
              {opt.label}
              <button
                type="button"
                className="ml-0.5 rounded-full hover:text-destructive"
                onClick={() => toggle(opt.value)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Value Input — renders the right picker for a given (field, operator)
// =============================================================================

function ValueInput({
  field,
  operator,
  value,
  onChange,
}: {
  field: AssignmentRuleField;
  operator: ConditionOperator;
  value: string | string[];
  onChange: (value: string | string[]) => void;
}): React.ReactElement {
  const isMulti =
    operator === 'in' ||
    operator === 'not_in' ||
    operator === 'contains_any' ||
    operator === 'contains_all';

  if (field === 'priority' && !isMulti) {
    return (
      <Select value={typeof value === 'string' ? value : (value[0] ?? '')} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-full text-xs">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {PRIORITY_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (field === 'priority' && isMulti) {
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    return (
      <div className="flex flex-wrap gap-1">
        {PRIORITY_OPTIONS.map((opt) => {
          const isSelected = selected.includes(opt.value);
          return (
            <Button
              key={opt.value}
              type="button"
              variant="outline"
              size="sm"
              className={cn('h-7 text-xs', isSelected && SELECTED_OUTLINE_BUTTON)}
              onClick={() => {
                if (isSelected) {
                  onChange(selected.filter((v) => v !== opt.value));
                } else {
                  onChange([...selected, opt.value]);
                }
              }}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>
    );
  }

  if (field === 'provider') {
    if (isMulti) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      return <ProviderPicker multiple selected={selected} onChange={onChange} />;
    }
    return (
      <ProviderPicker
        selected={typeof value === 'string' ? value : (value[0] ?? '')}
        onChange={onChange}
      />
    );
  }

  if (field === 'location') {
    if (isMulti) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      return <LocationPicker multiple selected={selected} onChange={onChange} />;
    }
    return (
      <LocationPicker
        selected={typeof value === 'string' ? value : (value[0] ?? '')}
        onChange={onChange}
      />
    );
  }

  if (field === 'insurance') {
    if (isMulti) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      return <InsurancePicker multiple selected={selected} onChange={onChange} />;
    }
    return (
      <InsurancePicker
        selected={typeof value === 'string' ? value : (value[0] ?? '')}
        onChange={onChange}
      />
    );
  }

  if (field === 'label') {
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    return <LabelMultiSelect selected={selected} onChange={onChange} />;
  }

  // Free-text fallback — diagnoses, services, etc.
  if (isMulti) {
    const strValue = Array.isArray(value) ? value.join(', ') : value;
    return (
      <Input
        value={strValue}
        onChange={(e) => {
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(parts.length > 0 ? parts : e.target.value);
        }}
        placeholder="Comma-separated values..."
        className="h-9 text-xs"
      />
    );
  }

  return (
    <Input
      value={typeof value === 'string' ? value : (value[0] ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Value..."
      className="h-9 text-xs"
    />
  );
}

// =============================================================================
// RuleCondition — leaf editor (one row in the editor)
// =============================================================================

function RuleCondition({
  node,
  onChange,
  onRemove,
  showErrors,
}: {
  node: EditorCondition;
  onChange: (next: EditorNode) => void;
  onRemove: () => void;
  /**
   * When true, render inline error states for missing field / operator
   * / value. Set by RuleEditor after the user attempts to save with
   * incomplete data. Stays false until the first failed save attempt
   * so a freshly-added row doesn't immediately scream at the user.
   */
  showErrors: boolean;
}): React.ReactElement {
  const operators = node.field ? (OPERATORS_BY_FIELD[node.field] ?? []) : [];

  const handleFieldChange = (fieldKey: AssignmentRuleField): void => {
    const newOps = OPERATORS_BY_FIELD[fieldKey] ?? [];
    const newOp: ConditionOperator = newOps[0]?.value ?? 'equals';
    onChange({ kind: 'condition', field: fieldKey, operator: newOp, value: '' });
  };

  const handleOpChange = (op: ConditionOperator): void => {
    onChange({ kind: 'condition', field: node.field, operator: op, value: '' });
  };

  // Per-cell error flags. Each becomes true only when (a) save was
  // attempted AND (b) the cell is in an empty/unset state. Picking
  // the field auto-sets the operator (first available), so the
  // operator-error case is rare in practice but kept for completeness.
  const fieldError = showErrors && !node.field;
  const operatorError = showErrors && !!node.field && !node.operator;
  const valueError =
    showErrors &&
    !!node.field &&
    !!node.operator &&
    (Array.isArray(node.value) ? node.value.length === 0 : node.value === '');

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,3fr)_36px] items-start gap-2">
        {/* Field */}
        <div className="space-y-1">
          <Select
            value={node.field || undefined}
            onValueChange={(v) => handleFieldChange(v as AssignmentRuleField)}
          >
            <SelectTrigger
              className={cn(
                'h-9 w-full text-xs',
                fieldError && 'border-destructive ring-1 ring-destructive/30'
              )}
              aria-invalid={fieldError || undefined}
            >
              <SelectValue placeholder="Select field..." />
            </SelectTrigger>
            <SelectContent>
              {FIELDS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldError && <p className="text-[11px] text-destructive">Pick a field</p>}
        </div>

        {/* Operator + Value (or empty spacer). Inside this branch
            `node.field` is narrowed to `AssignmentRuleField` (non-null)
            via the truthy check — keeps the EditorCondition type honest
            without falling back to `as` casts. */}
        {node.field ? (
          <>
            <div className="space-y-1">
              <Select
                value={node.operator || undefined}
                onValueChange={(v) => handleOpChange(v as ConditionOperator)}
              >
                <SelectTrigger
                  className={cn(
                    'h-9 w-full text-xs',
                    operatorError && 'border-destructive ring-1 ring-destructive/30'
                  )}
                  aria-invalid={operatorError || undefined}
                >
                  <SelectValue placeholder="Operator..." />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {operatorError && <p className="text-[11px] text-destructive">Pick an operator</p>}
            </div>
            <div className="space-y-1">
              {node.operator ? (
                <div
                  className={cn(
                    valueError && 'rounded-md ring-1 ring-destructive/40 ring-offset-1'
                  )}
                  aria-invalid={valueError || undefined}
                >
                  <ValueInput
                    field={node.field}
                    operator={node.operator}
                    value={node.value}
                    onChange={(v) =>
                      onChange({
                        kind: 'condition',
                        field: node.field,
                        operator: node.operator,
                        value: v,
                      })
                    }
                  />
                </div>
              ) : (
                <div />
              )}
              {valueError && <p className="text-[11px] text-destructive">Pick a value</p>}
            </div>
          </>
        ) : (
          <div className="col-span-2" />
        )}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
          aria-label="Remove condition"
        >
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// RuleGroup — recursive group editor
// =============================================================================

interface RuleGroupProps {
  node: EditorGroup;
  onChange: (next: EditorNode) => void;
  /** 1 = root group; depth ≥ 1 always. */
  depth: number;
  /** Root group can't be removed; nested groups can. */
  onRemove?: () => void;
  /** Forwarded down so per-leaf and per-group error states render. */
  showErrors: boolean;
}

function RuleGroup({
  node,
  onChange,
  depth,
  onRemove,
  showErrors,
}: RuleGroupProps): React.ReactElement {
  const isRoot = depth === 1;
  const canNest = depth < ASSIGNMENT_RULE_MAX_DEPTH;
  const canAdd = node.children.length < ASSIGNMENT_RULE_MAX_CHILDREN;
  // Group-level errors:
  //  - Empty root with showErrors → "add at least one condition"
  //  - Empty nested group with showErrors → same message; the group
  //    will also be rejected by the server's `hasEmptyNestedGroup`
  //    refine, but surfacing it inline is faster feedback.
  const emptyError = showErrors && node.children.length === 0;

  const updateChild = (index: number, next: EditorNode): void => {
    const children = [...node.children];
    children[index] = next;
    onChange({ ...node, children });
  };

  const removeChild = (index: number): void => {
    onChange({ ...node, children: node.children.filter((_, i) => i !== index) });
  };

  const addCondition = (): void => {
    onChange({ ...node, children: [...node.children, emptyCondition()] });
  };

  const addNestedGroup = (): void => {
    // Default the new group to the opposite of this group's op —
    // same-op nesting is always redundant (an `any` inside an `any`
    // collapses to a single any-group), so flipping is the useful
    // default. User can still toggle the new group's All/Any header.
    onChange({
      ...node,
      children: [...node.children, emptyGroup(node.op === 'all' ? 'any' : 'all')],
    });
  };

  const setOp = (op: 'all' | 'any'): void => {
    onChange({ ...node, op });
  };

  return (
    <div
      className={cn(
        'space-y-2 rounded-lg border p-3',
        isRoot ? 'border-dashed' : 'border-l-4 border-l-primary/50 bg-muted/30',
        emptyError && 'border-destructive ring-1 ring-destructive/30'
      )}
    >
      {/* Group header — All/Any toggle, optional remove */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-muted-foreground">Match</span>
          <Select value={node.op} onValueChange={(v) => setOp(v as 'all' | 'any')}>
            <SelectTrigger className="h-7 w-[72px] text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="any">Any</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-muted-foreground">of:</span>
        </div>
        {!isRoot && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            // Same dimensions as the row-level remove button (`h-9 w-9`
            // in RuleCondition) so within a group the header's X lines
            // up size-wise with the rows' X icons below it.
            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
            aria-label="Remove group"
          >
            <X size={14} />
          </Button>
        )}
      </div>

      {/* Children */}
      <div className="space-y-2">
        {node.children.map((child, i) =>
          child.kind === 'condition' ? (
            <RuleCondition
              key={i}
              node={child}
              onChange={(next) => updateChild(i, next)}
              onRemove={() => removeChild(i)}
              showErrors={showErrors}
            />
          ) : (
            <RuleGroup
              key={i}
              node={child}
              depth={depth + 1}
              onChange={(next) => updateChild(i, next)}
              onRemove={() => removeChild(i)}
              showErrors={showErrors}
            />
          )
        )}
        {emptyError ? (
          <p className="px-1 text-xs text-destructive">
            {isRoot
              ? 'Add at least one condition to this rule.'
              : 'This group needs at least one condition or remove it.'}
          </p>
        ) : node.children.length === 0 ? (
          <p className="px-1 text-xs italic text-muted-foreground">No conditions yet</p>
        ) : null}
      </div>

      {/* Add buttons */}
      {canAdd && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={addCondition}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            <Plus size={12} />
            Condition
          </button>
          {canNest && (
            <button
              type="button"
              onClick={addNestedGroup}
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
            >
              <FolderTree size={12} />
              Group
            </button>
          )}
          {!canNest && (
            <span
              className="inline-flex items-center text-xs italic text-muted-foreground"
              title={`Maximum nesting depth (${ASSIGNMENT_RULE_MAX_DEPTH}) reached.`}
            >
              max nesting reached
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Live English summary — recursive
// =============================================================================

const FIELD_LABELS: Record<AssignmentRuleField, string> = {
  priority: 'Priority',
  insurance: 'Insurance',
  provider: 'Provider',
  diagnoses: 'Diagnoses',
  services: 'Services',
  location: 'Location',
  label: 'Label',
};

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  in: 'is one of',
  not_in: 'is not one of',
  contains_any: 'contains any of',
  contains_all: 'contains all of',
};

/**
 * Format a condition value for the live-preview sentence above the
 * editor. Diverges from `RuleSummary.formatConditionValue` only in the
 * empty/placeholder case (`'...'` vs `''`) and the truncation suffix
 * (`+N more` vs `+N`) — both intentionally distinct and pinned by
 * `condition-builder.test.ts`.
 *
 * Exported for unit testing only; consumers render via `ConditionBuilder`.
 */
export function formatForSentence(
  field: string,
  value: string | string[],
  resolve: (field: string, raw: string) => string
): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '...';
    const resolved = value.map((v) => resolve(field, v));
    if (resolved.length <= 2) return resolved.join(', ');
    return `${resolved[0]}, ${resolved[1]}, +${resolved.length - 2} more`;
  }
  return value ? resolve(field, value) : '...';
}

function summarizeNode(
  node: EditorNode,
  resolve: (field: string, value: string) => string
): React.ReactNode {
  if (node.kind === 'condition') {
    if (!node.field || !node.operator) {
      return <span className="italic text-muted-foreground">select a field</span>;
    }
    const fieldLabel = FIELD_LABELS[node.field] ?? node.field;
    const opLabel = OPERATOR_LABELS[node.operator] ?? node.operator;
    const displayValue = formatForSentence(node.field, node.value, resolve);
    return (
      <>
        <strong className="font-semibold">{fieldLabel}</strong> {opLabel}{' '}
        <strong className="font-semibold">{displayValue}</strong>
      </>
    );
  }

  if (node.children.length === 0) {
    return <span className="italic text-muted-foreground">empty group</span>;
  }

  const joinerWord = node.op === 'all' ? ' and ' : ' or ';
  const joiner = (
    <span className="font-medium uppercase tracking-wide text-muted-foreground">{joinerWord}</span>
  );

  return (
    <>
      <span className="text-muted-foreground">(</span>
      {node.children.map((child, i) => (
        <span key={i}>
          {i > 0 && joiner}
          {summarizeNode(child, resolve)}
        </span>
      ))}
      <span className="text-muted-foreground">)</span>
    </>
  );
}

/**
 * Past depth 3, parenthesized prose like
 *   `((A AND B) OR (C AND (D OR E)))` becomes unreadable as a sentence.
 * Cap the live preview at depth 3 and fall back to a compact note that
 * defers verification to the editor structure above. The next PR's
 * LLM-cached summary replaces this with proper natural-language prose
 * for arbitrary depth.
 */
const SENTENCE_DEPTH_LIMIT = 3;

function ConditionSentence({ root }: { root: EditorNode }): React.ReactElement | null {
  const resolve = useConditionValueResolver();
  if (root.kind === 'group' && root.children.length === 0) return null;

  // ruleNodeDepth accepts AssignmentRuleNode, but EditorNode is a
  // superset — at runtime the shape is identical, only field/operator
  // may be null on leaves. Cast for the depth walk; the function only
  // recurses on group children and never touches the nullable fields.
  if (ruleNodeDepth(root as unknown as AssignmentRuleNode) > SENTENCE_DEPTH_LIMIT) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-3 text-xs italic text-muted-foreground">
        Rule structure is too deeply nested to summarize in plain English. Verify against the groups
        below.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm leading-relaxed">
      <span className="font-medium text-muted-foreground">If </span>
      {summarizeNode(root, resolve)}
    </div>
  );
}

// =============================================================================
// Main entry — ConditionBuilder
// =============================================================================

interface ConditionBuilderProps {
  /**
   * The rule's condition tree. Always a group at the root — even
   * single-condition rules are wrapped in a single-child `all`
   * group so the editor has a stable place to render the All/Any
   * header. Pass an empty group to render the empty-state.
   *
   * Accepts `EditorNode` so the editor can hold partial leaves
   * (field/operator unset). The caller's state should typically
   * also be `EditorNode`; an `AssignmentRuleNode` from the API is
   * structurally compatible (no null fields → narrower type).
   */
  root: EditorNode;
  onChange: (next: EditorNode) => void;
  /**
   * When true, render inline error states for any incomplete leaf
   * or empty group. The parent should set this to true after the
   * first failed save attempt and clear it once the user fixes the
   * tree (or the form resets). Defaults to false so a fresh editor
   * doesn't immediately scream at the user.
   */
  showErrors?: boolean;
}

export function ConditionBuilder({
  root,
  onChange,
  showErrors = false,
}: ConditionBuilderProps): React.ReactElement {
  // Defensive normalization: if a caller hands us a bare condition
  // (legacy or test fixture), wrap it in a single-child root group so
  // the editor always shows the precedence header.
  const rootGroup: EditorGroup =
    root.kind === 'group' ? root : { kind: 'group', op: 'all', children: [root] };

  return (
    <div className="space-y-3">
      <ConditionSentence root={rootGroup} />
      <RuleGroup node={rootGroup} depth={1} onChange={onChange} showErrors={showErrors} />
    </div>
  );
}
